import { BellIcon } from '@design-systems/react-icons'
import { AccountCard } from './AccountCard'
import { Caption, Grid, InputRadio } from '@design-systems/canvas-ui'
import { useWatch, useController, type Control } from 'react-hook-form'
import ControlledFormField from 'components/form/ControlledFormField'
import { AccountSettingsInputs } from 'account/types'

type Props = {
  control: Control<AccountSettingsInputs>
}

export const NotificationsCard = ({ control }: Props) => {
  const currentNewProductSamples = useWatch({
    control,
    name: 'notifyNewProductSamples',
  })
  const currentCancelledProductSamples = useWatch({
    control,
    name: 'notifyCancelledProductSamples',
  })
  const currentClaimChanges = useWatch({ control, name: 'notifyClaimChanges' })

  const { field: emailFrequencyField } = useController({
    control,
    name: 'emailFrequency',
  })

  return (
    <AccountCard
      svg={BellIcon}
      cardHeading="Notifications"
      cardSubheading="Choose which notifications you receive and how often email updates are sent."
    >
      <Grid.Container spacing="expanded">
        <Grid.Item size={{ xs: 12, sm: 6 }}>
          <Grid.Container>
            <Grid.Item size={{ xs: 12 }}>
              <Caption below="Get notified about current work and requested actions.">
                <strong>Notification types</strong>
              </Caption>
            </Grid.Item>
            <Grid.Item size={{ xs: 12 }}>
              <ControlledFormField
                control={control}
                type="switch"
                name="notifyNewProductSamples"
                id="notifyNewProductSamples"
                label="New product samples"
                isChecked={currentNewProductSamples}
              />
            </Grid.Item>
            <Grid.Item size={{ xs: 12 }}>
              <ControlledFormField
                control={control}
                type="switch"
                name="notifyCancelledProductSamples"
                id="notifyCancelledProductSamples"
                label="Cancelled product samples"
                isChecked={currentCancelledProductSamples}
              />
            </Grid.Item>
            <Grid.Item size={{ xs: 12 }}>
              <ControlledFormField
                control={control}
                type="switch"
                name="notifyClaimChanges"
                id="notifyClaimChanges"
                label="Claim changes"
                isChecked={currentClaimChanges}
              />
            </Grid.Item>
          </Grid.Container>
        </Grid.Item>
        {/* Disabling frequency until CNS supports it */}
        {/* eslint-disable-next-line */}
        {false && (
          <Grid.Item size={{ xs: 12, sm: 6 }}>
            <Grid.Container>
              <Grid.Item size={{ xs: 12 }}>
                <Caption below="Change how often you receive email updates.">
                  <strong>Email frequency</strong>
                </Caption>
              </Grid.Item>
              <Grid.Item size={{ xs: 12 }}>
                <Grid.Container direction="column">
                  <Grid.Item>
                    <Caption below="Send an email as soon as each update happens.">
                      <InputRadio
                        id="emailFrequency-immediate"
                        name={emailFrequencyField.name}
                        value="immediate"
                        label="Send emails immediately"
                        isChecked={emailFrequencyField.value === 'immediate'}
                        onChange={() =>
                          emailFrequencyField.onChange('immediate')
                        }
                        onBlur={emailFrequencyField.onBlur}
                      />
                    </Caption>
                  </Grid.Item>
                  <Grid.Item>
                    <Caption below="Group updates into a single email every few hours. In-app notifications still appear right away.">
                      <InputRadio
                        id="emailFrequency-digest"
                        name={emailFrequencyField.name}
                        value="digest"
                        label="Send a digest every few hours"
                        isChecked={emailFrequencyField.value === 'digest'}
                        onChange={() => emailFrequencyField.onChange('digest')}
                        onBlur={emailFrequencyField.onBlur}
                      />
                    </Caption>
                  </Grid.Item>
                </Grid.Container>
              </Grid.Item>
            </Grid.Container>
          </Grid.Item>
        )}
      </Grid.Container>
    </AccountCard>
  )
}

export default NotificationsCard
